import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import css from "./ClientsCreateInfoModal.module.css";
import { clientsActions } from "../../../../store/slices/clients-slices";
import { ModalSelectDeliveryInterval } from "../ModalSelectDeliveryInterval/ModalSelectDeliveryInterval";
import { ModalSelectCourier } from "../ModalSelectCourier/ModalSelectCourier";
import { ModalExcludedProduct } from "../ModalExcludedProduct/ModalExcludedProduct";
import { clientsService } from "../../../../services/clients-services";
import { YandexMapSelector } from "../../../ui/YandexMapSelector/YandexMapSelector";
import { getConfig } from "../../../../config";
import { adminUsersService } from "../../../../services/admin-services/admin-users.service";

function ClientsCreateInfoModal({
  setIsOpenClientsInfoModal,
  phoneNumber,
  client,
  setIsOpenUpdate,
  page,
  clientDefaults,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const phonePrefix = useMemo(() => {
    return getConfig().cityCode === "UAE" ? "+971" : "+995";
  }, []);

  const methods = useForm({
    defaultValues: {
      contact: client?.phoneNumber || (!client ? (clientDefaults?.phoneCode || phonePrefix) : phonePrefix),
      name: client?.clientName || "",
      communicationMethod: client?.communicationMethod || (!client ? clientDefaults?.communicationMethod : "") || "",
      communicationDetails: client?.communicationDetails || (!client ? clientDefaults?.communicationDetails : "") || "",
      city: client?.devlieryCity || (!client ? clientDefaults?.city : "") || "",
      address: client?.deliveryStreet || "",
      building: client?.buidlingNumber || "",
      entrance: client?.entranceNumber || "",
      floor: client?.floorNumber || "",
      apartment: client?.apartmnetNumber || "",
      comment: client?.commentForDelivery || "",
      managerComment: client?.commentForKitchenOrManager || (!client ? clientDefaults?.managerComment : "") || "",
      deliveryInterval: client?.deleveryInterval || (!client ? clientDefaults?.deliveryInterval : "") || "",
      courier: client?.courier || "",
      points: client?.points || 0,
      roles: client?.role || "",
      latitude: client?.latitude || (!client ? clientDefaults?.defaultLat : null) || 50.4501,
      longitude: client?.longitude || (!client ? clientDefaults?.defaultLng : null) || 30.5234,
    },
    mode: "onChange",
  });

  const { register, handleSubmit, setValue, reset } = methods;

  const [communicationMethods, setCommunicationMethods] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(
    client?.excludedProducts || []
  );
  const [selectedIds, setSelectedIds] = useState(
    client?.excludedProducts?.map((product) => product.id) || []
  );
  const [coordinates, setCoordinates] = useState({
    lat: client?.latitude || 50.4501,
    lng: client?.longitude || 30.5234,
  });

  useEffect(() => {
    const fetchCommunicationMethods = async () => {
      try {
        const methods = await clientsService.getCommunicationMethods();
        setCommunicationMethods(methods);
      } catch (error) {
        console.error("Error fetching communication methods:", error);
        setCommunicationMethods([
          "PHONE",
          "TELEGRAM",
          "WHATSAPP",
          "INSTAGRAM",
          "EMAIL",
        ]);
      }
    };

    fetchCommunicationMethods();

    if (client) {
      setCoordinates({
        lat: client.latitude || 50.4501,
        lng: client.longitude || 30.5234,
      });
      setSelectedMeals(client.excludedProducts || []);
      setSelectedIds(client.excludedProducts?.map((p) => p.id) || []);
    }
  }, [client]);

  useEffect(() => {
    if (client && communicationMethods.length > 0) {
      reset({
        contact: client.phoneNumber || phonePrefix,
        name: client.clientName || "",
        communicationMethod: client.communicationMethod || "",
        communicationDetails: client.communicationDetails || "",
        city: client.devlieryCity || "",
        address: client.deliveryStreet || "",
        building: client.buidlingNumber || "",
        entrance: client.entranceNumber || "",
        floor: client.floorNumber || "",
        apartment: client.apartmnetNumber || "",
        comment: client.commentForDelivery || "",
        managerComment: client.commentForKitchenOrManager || "",
        deliveryInterval: client.deleveryInterval || "",
        courier: client.courier || "",
        points: client.points || 0,
        roles: client.role || "",
        latitude: client.latitude || 50.4501,
        longitude: client.longitude || 30.5234,
      });
    } else if (!client) {
      setValue("contact", phoneNumber || clientDefaults?.phoneCode || phonePrefix);
      setValue("communicationMethod", clientDefaults?.communicationMethod || "");
      setValue("communicationDetails", clientDefaults?.communicationDetails || "");
      setValue("city", clientDefaults?.city || "");
      setValue("managerComment", clientDefaults?.managerComment || "");
      setValue("deliveryInterval", clientDefaults?.deliveryInterval || "");
    }
  }, [client, communicationMethods, reset, phoneNumber, phonePrefix, setValue, clientDefaults]);

  const handleLocationSelect = (lat, lng) => {
    setCoordinates({ lat, lng });
    setValue("latitude", lat);
    setValue("longitude", lng);
  };

  const onSubmit = async (data) => {
    try {
      const phoneOrEmailProvided = Boolean(
        (data.contact && String(data.contact).trim()) ||
          (data.email && String(data.email).trim())
      );
      if (!phoneOrEmailProvided) {
        toast.error("Provide at least phoneNumber or email");
        return;
      }

      const formattedData = {
        phoneNumber:
          String(
            phoneNumber || client?.phoneNumber || data.contact || ""
          ).trim() || undefined,
        clientName: data.name || undefined,
        communicationMethod: data.communicationMethod || undefined,
        communicationDetails: data.communicationDetails || undefined,
        devlieryCity: data.city || undefined,
        deliveryStreet: data.address || undefined,
        buidlingNumber: data.building || undefined,
        entranceNumber: data.entrance || undefined,
        floorNumber: data.floor || undefined,
        apartmnetNumber: data.apartment || undefined,
        commentForDelivery: data.comment || undefined,
        commentForKitchenOrManager: data.managerComment || undefined,
        deleveryInterval: data.deliveryInterval || undefined,
        courier: data.courier || undefined,
        points:
          data.points !== undefined && data.points !== null
            ? Number(data.points)
            : undefined,
        role: data.roles || undefined,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        productIds: selectedIds ?? [],
      };

      if (client && client.id) {
        await adminUsersService.updateById(client.id, formattedData);
        dispatch(clientsActions.changeTrigger());
        toast.success(t("updated-success"));
        setIsOpenUpdate && setIsOpenUpdate(false);
      } else {
        await adminUsersService.createOrUpsert(formattedData);
        dispatch(clientsActions.changeTrigger());
        toast.success(t("created-success"));
        setIsOpenClientsInfoModal && setIsOpenClientsInfoModal(false);
      }
    } catch (error) {
      console.error(t("error"), error);
      const msg =
        error?.response?.data?.message || error?.message || t("error");
      toast.error(msg);
    }
  };

  return (
    <div
      className={css.modalOverlay}
      onClick={() => {
        if (client) {
          setIsOpenUpdate && setIsOpenUpdate(false);
        } else {
          setIsOpenClientsInfoModal && setIsOpenClientsInfoModal(false);
        }
      }}
    >
      <div className={css.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <h3>{client ? t("edit-client") : t("create-client")}</h3>

          <button
            className={css.closeButton}
            onClick={() => {
              if (client) {
                setIsOpenUpdate && setIsOpenUpdate(false);
              } else {
                setIsOpenClientsInfoModal && setIsOpenClientsInfoModal(false);
              }
            }}
          >
            &times;
          </button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={css.form}>
            <div className={css.formRow}>
              <label htmlFor="contact">{t("contact")}</label>
              <div className={css.contactContainer}>
                <input
                  id="contact"
                  name="contact"
                  type="text"
                  defaultValue={phoneNumber || phonePrefix}
                  {...register("contact", {
                    pattern: {
                      value: /^\+?\d+$/,
                      message: "Invalid phone number",
                    },
                  })}
                  disabled
                />
                <button type="button" className={css.smsCodeButton}>
                  {t("show-sms-code")}
                </button>
              </div>
            </div>

            <div className={css.formRow}>
              <label htmlFor="name">{t("modal-name-input-new-category")}</label>
              <input
                id="name"
                name="name"
                type="text"
                {...register("name", { required: true })}
              />
            </div>

            <div className={css.formRow}>
              <label htmlFor="communicationMethod">
                {t("communication-method")}
              </label>
              <select
                id="communicationMethod"
                name="communicationMethod"
                {...register("communicationMethod", { required: true })}
              >
                <option value=""></option>
                {communicationMethods.map((method) => (
                  <option key={method} value={method}>
                    {method.charAt(0).toUpperCase() +
                      method.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className={css.formRow}>
              <label htmlFor="communicationDetails">
                {t("communication-details")}
              </label>
              <input
                id="communicationDetails"
                name="communicationDetails"
                type="text"
                placeholder={t("communication-details-placeholder")}
                {...register("communicationDetails", { required: true })}
              />
            </div>

            <div className={css.formRow}>
              <label htmlFor="city">{t("city")}</label>
              <input
                id="city"
                name="city"
                type="text"
                {...register("city", { required: true })}
              />
            </div>

            <div className={css.formRow}>
              <label htmlFor="address">{t("address")}</label>
              <div className={css.addressFields}>
                <div className={css.addressField}>
                  <label htmlFor="address" className={css.fieldLabel}>
                    {t("street")}
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    placeholder={t("street")}
                    {...register("address", { required: true })}
                  />
                </div>
                <div className={css.addressField}>
                  <label htmlFor="building" className={css.fieldLabel}>
                    {t("building")}
                  </label>
                  <input
                    id="building"
                    name="building"
                    type="text"
                    placeholder={t("building")}
                    {...register("building", { required: true })}
                  />
                </div>
                <div className={css.addressField}>
                  <label htmlFor="entrance" className={css.fieldLabel}>
                    {t("entrance")}
                  </label>
                  <input
                    id="entrance"
                    name="entrance"
                    type="text"
                    placeholder={t("entrance")}
                    {...register("entrance", { required: true })}
                  />
                </div>
                <div className={css.addressField}>
                  <label htmlFor="floor" className={css.fieldLabel}>
                    {t("floor")}
                  </label>
                  <input
                    id="floor"
                    name="floor"
                    type="text"
                    placeholder={t("floor")}
                    {...register("floor", { required: true })}
                  />
                </div>
                <div className={css.addressField}>
                  <label htmlFor="apartment" className={css.fieldLabel}>
                    {t("apartment")}
                  </label>
                  <input
                    id="apartment"
                    name="apartment"
                    type="text"
                    placeholder={t("apartment")}
                    {...register("apartment", { required: true })}
                  />
                </div>
              </div>
            </div>

            <div className={css.formRow}>
              <label htmlFor="comment">{t("comment")}</label>
              <textarea id="comment" name="comment" {...register("comment")} />
            </div>

            <div className={css.formRow}>
              <label htmlFor="managerComment">{t("comment-for-manager")}</label>
              <textarea
                id="managerComment"
                name="managerComment"
                {...register("managerComment")}
              />
            </div>

            <div className={css.formRow}>
              <label>{t("location-on-map")}</label>
              <div className={css.mapContainer}>
                <YandexMapSelector
                  initialLat={coordinates.lat}
                  initialLng={coordinates.lng}
                  onLocationSelect={handleLocationSelect}
                />
              </div>
            </div>

            <div className={css.formRow}>
              <ModalSelectDeliveryInterval />
            </div>

            <div className={css.formRow}>
              <ModalSelectCourier />
            </div>

            <div className={css.formRow}>
              <ModalExcludedProduct
                setSelectedMeals={setSelectedMeals}
                setSelectedIds={setSelectedIds}
                selectedMeals={selectedMeals}
              />
            </div>

            <div className={css.formRow}>
              <label htmlFor="points">{t("points")}</label>
              <input
                id="points"
                name="points"
                type="number"
                min="0"
                step="1"
                {...register("points", { valueAsNumber: true })}
              />
            </div>

            <div className={css.formRow}>
              <label htmlFor="roles">{t("roles")}</label>
              <select
                id="roles"
                name="roles"
                {...register("roles", { required: true })}
              >
                <option value=""></option>
                <option value="User">User</option>
                <option value="Admin">Admin</option>
                <option value="Manager">Manager</option>
                <option value="Courier">Courier</option>
                <option value="Nutritionist">Nutritionist</option>
              </select>
            </div>

            <div className={css.formActions}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={() => {
                  if (client) {
                    setIsOpenUpdate && setIsOpenUpdate(false);
                  } else {
                    setIsOpenClientsInfoModal &&
                      setIsOpenClientsInfoModal(false);
                  }
                }}
              >
                {t("cancel")}
              </button>

              <button type="submit" className={css.saveButton}>
                {client ? t("update") : t("save")}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}

export default ClientsCreateInfoModal;
